/**
 * EmailDomainCard · Feb 2026 · Iter 211
 * ─────────────────────────────────────────────────────────────────────
 * Seller-facing UI for wiring their Resend sending domain. Rendered
 * ABOVE the storefront custom-domain card because the backend now
 * blocks saving the storefront domain until this email domain is
 * verified — closes the trust gap where `shop.brand.com` served pages
 * but emails still came from `notifications@verqo.com`.
 *
 * States (driven by `status` from GET /tenant/email-domain/status):
 *   · null / no record  → empty state, "Add sending domain" input
 *   · not_started       → DNS records table + "Verify DNS" CTA
 *   · pending           → DNS records table + auto-poll every 15s
 *   · verified          → green success card, "Disconnect" button
 *   · failure / failed  → red banner + retry CTA
 */
import { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Mail, Copy, Loader2, CheckCircle2, AlertTriangle, Trash2,
  RefreshCcw, ShieldCheck, Info,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const STATUS_STYLES = {
  verified:    { fg: "text-emerald-800", bg: "bg-emerald-50",  border: "border-emerald-200", label: "Verified" },
  pending:     { fg: "text-amber-800",   bg: "bg-amber-50",    border: "border-amber-200",   label: "Pending DNS" },
  not_started: { fg: "text-slate-800",   bg: "bg-slate-100",   border: "border-slate-200",   label: "Not started" },
  failure:     { fg: "text-rose-800",    bg: "bg-rose-50",     border: "border-rose-200",    label: "Failure" },
  failed:      { fg: "text-rose-800",    bg: "bg-rose-50",     border: "border-rose-200",    label: "Failed" },
};

// Compute a sensible sending-subdomain default from the seller's
// storefront custom domain: `brand.com` → `mail.brand.com`.
function suggestSendingSubdomain(customDomain) {
  if (!customDomain) return "";
  const stripped = customDomain.replace(/^https?:\/\//i, "").split("/")[0];
  // If they already gave us a subdomain like `shop.brand.com` we keep
  // the apex + prepend `mail.` (mail.brand.com), never mail.shop.brand.com.
  const parts = stripped.split(".");
  const apex = parts.length > 2 ? parts.slice(-2).join(".") : stripped;
  return `mail.${apex}`;
}

export default function EmailDomainCard({ customDomain, onStatusChange }) {
  const [state, setState] = useState({ id: null, name: null, status: null, records: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState("");
  const pollRef = useRef(null);

  // Push status changes up to the parent so it can gate the Custom
  // Domain "Save" button and swap red-flag banners.
  useEffect(() => {
    onStatusChange?.({
      id:     state.id,
      name:   state.name,
      status: state.status,
    });
  }, [state.id, state.name, state.status]); // eslint-disable-line

  const load = async () => {
    try {
      const r = await api.get("/tenant/email-domain/status");
      setState(r.data || { id: null, status: null, records: [] });
    } catch (e) {
      // Non-fatal — the section just shows the empty state.
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Pre-fill the input with mail.<customDomain> once we know it.
  useEffect(() => {
    if (!state.id && !input && customDomain) {
      setInput(suggestSendingSubdomain(customDomain));
    }
  }, [customDomain, state.id]); // eslint-disable-line

  // Auto-poll while pending — stop when status flips or after 2 min.
  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (state.status === "pending" || state.status === "not_started") {
      const startedAt = Date.now();
      pollRef.current = setInterval(async () => {
        if (Date.now() - startedAt > 120_000) {
          clearInterval(pollRef.current); pollRef.current = null; return;
        }
        try {
          const r = await api.get("/tenant/email-domain/status");
          setState(r.data);
          if (r.data?.status === "verified" || r.data?.status === "failure") {
            clearInterval(pollRef.current); pollRef.current = null;
          }
        } catch {}
      }, 15_000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [state.status]);

  const create = async () => {
    if (!input.trim()) return toast.error("Enter a sending domain first");
    setBusy(true);
    try {
      const r = await api.post("/tenant/email-domain/create", { domain: input.trim().toLowerCase() });
      setState(r.data);
      toast.success("Sending domain registered — publish the DNS records below");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d :
                  d?.resend_body?.message || d?.message || "Could not register domain");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const r = await api.post("/tenant/email-domain/verify");
      setState(r.data);
      if (r.data?.status === "verified") {
        toast.success("Email domain verified — you're fully whitelabeled!");
      } else {
        toast(`DNS check ran · status: ${r.data?.status || "pending"}`, { icon: "⏳" });
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.resend_body?.message || d?.message || "Verify failed");
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm(
      "Disconnect your email sending domain?\n\nAll buyer-facing emails will revert to being sent from Verqo. " +
      "If you're currently using a storefront custom domain, remove that first — otherwise your buyers will " +
      "see a whitelabeled site but Verqo-branded emails."
    )) return;
    setBusy(true);
    try {
      await api.delete("/tenant/email-domain");
      setState({ id: null, name: null, status: null, records: [] });
      toast.success("Sending domain disconnected");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "site_domain_still_active") {
        toast.error(d.message);
      } else {
        toast.error(d?.message || "Could not disconnect");
      }
    } finally { setBusy(false); }
  };

  const style = STATUS_STYLES[state.status] || STATUS_STYLES.not_started;

  return (
    <div className="rounded-2xl border border-border bg-white p-5 space-y-4" data-testid="email-domain-card">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-700 grid place-items-center shrink-0">
          <Mail className="w-4 h-4"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[14px] font-semibold">Email sending domain</div>
            {state.id && (
              <span data-testid="email-domain-status-pill"
                    className={`inline-flex items-center gap-1 text-[10px] mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full ${style.bg} ${style.fg} border ${style.border}`}>
                {state.status === "verified" && <CheckCircle2 className="w-2.5 h-2.5"/>}
                {(state.status === "failure" || state.status === "failed") && <AlertTriangle className="w-2.5 h-2.5"/>}
                {style.label}
              </span>
            )}
          </div>
          <div className="text-[12px] text-foreground/60 mt-0.5 max-w-2xl">
            White-labels every buyer-facing email so recipients see&nbsp;
            <span className="mono">no-reply@{state.name || "yourbrand.com"}</span>&nbsp;
            instead of Verqo. Required before you can save your storefront custom domain.
          </div>
        </div>
      </div>

      {/* Storefront-domain-first callout */}
      {!customDomain && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2 text-[11.5px] flex items-start gap-2">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5"/>
          <div>
            <strong>Tip.</strong> Pick your storefront custom domain first (e.g. <span className="mono">brand.com</span>) —
            we'll auto-suggest <span className="mono">mail.brand.com</span> here.
            Both are required for full whitelabel.
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-6 grid place-items-center text-foreground/40">
          <Loader2 className="w-4 h-4 animate-spin"/>
        </div>
      ) : !state.id ? (
        /* Empty state */
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] text-foreground/60">Sending subdomain</Label>
            <Input
              data-testid="email-domain-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="mail.yourbrand.com"
              autoComplete="off"
              spellCheck={false}
            />
            <div className="text-[10.5px] text-foreground/50 mt-1">
              Use a subdomain (like <span className="mono">mail.brand.com</span>) — not your bare apex —
              so your real inbox at <span className="mono">you@brand.com</span> keeps working.
            </div>
          </div>
          <Button
            onClick={create}
            disabled={busy || !input.trim()}
            data-testid="email-domain-create"
            className="rounded-full h-9 px-4 text-[12.5px]"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> : <Mail className="w-3.5 h-3.5 mr-1.5"/>}
            Register sending domain
          </Button>
        </div>
      ) : state.status === "verified" ? (
        /* Verified state */
        <div className="space-y-3">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 p-3 flex items-start gap-2 text-[11.5px]">
            <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5"/>
            <div>
              <div className="font-medium">Every buyer email will now come from your brand.</div>
              <div className="mt-0.5 text-emerald-800/80">
                Sender: <span className="mono">no-reply@{state.name}</span>. You can now save your
                storefront custom domain in the section below.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={load} data-testid="email-domain-refresh"
                    className="rounded-full h-8 px-3 text-[12px]">
              <RefreshCcw className="w-3.5 h-3.5 mr-1"/>Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={disconnect} disabled={busy}
                    data-testid="email-domain-disconnect"
                    className="rounded-full h-8 px-3 text-[12px] text-rose-700 border-rose-200 hover:bg-rose-50">
              <Trash2 className="w-3.5 h-3.5 mr-1"/>Disconnect
            </Button>
          </div>
        </div>
      ) : (
        /* Not-started · Pending · Failure — show DNS records */
        <div className="space-y-3">
          <div className="rounded-lg border border-slate-200 bg-slate-50 text-slate-800 p-3 text-[11.5px]">
            <div className="font-medium mb-1">Publish these records at your DNS provider</div>
            <div className="text-slate-700/80">
              Log in to whoever manages DNS for <span className="mono">{state.name}</span> (Cloudflare,
              GoDaddy, Namecheap, Route53…) and add each row below. TTL <span className="mono">Auto</span> is fine.
              We&apos;ll re-check every 15 seconds — usually verifies within 5-10 minutes of publishing.
            </div>
          </div>

          <div className="rounded-lg border border-border overflow-hidden text-[11px]">
            <div className="grid grid-cols-[70px_90px_1fr_1fr_60px] bg-secondary/50 text-foreground/60 uppercase mono tracking-[0.14em] border-b border-border">
              <div className="px-2 py-1.5 border-r border-border">Type</div>
              <div className="px-2 py-1.5 border-r border-border">Record</div>
              <div className="px-2 py-1.5 border-r border-border">Name</div>
              <div className="px-2 py-1.5 border-r border-border">Value</div>
              <div className="px-2 py-1.5 text-center">Status</div>
            </div>
            {(state.records || []).map((r, i) => {
              const rStatus = r.status || "not_started";
              const ok = rStatus === "verified";
              return (
                <div key={i} data-testid={`email-domain-record-${i}`}
                     className="grid grid-cols-[70px_90px_1fr_1fr_60px] border-b last:border-b-0 border-border">
                  <div className="px-2 py-1.5 border-r border-border mono">{r.type}</div>
                  <div className="px-2 py-1.5 border-r border-border">{r.record}</div>
                  <div className="px-2 py-1.5 border-r border-border mono text-[10.5px] break-all">
                    <CopyableCell value={r.name}/>
                  </div>
                  <div className="px-2 py-1.5 border-r border-border mono text-[10.5px] break-all">
                    <CopyableCell value={r.value}/>
                    {r.priority && (
                      <span className="ml-1 text-foreground/50">(priority {r.priority})</span>
                    )}
                  </div>
                  <div className="px-2 py-1.5 text-center">
                    {ok
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 inline"/>
                      : <span className="text-[9.5px] mono uppercase tracking-[0.14em] text-amber-700">Pending</span>}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={verify}
              disabled={busy}
              data-testid="email-domain-verify"
              className="rounded-full h-9 px-4 text-[12.5px]"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> : <ShieldCheck className="w-3.5 h-3.5 mr-1.5"/>}
              Verify DNS
            </Button>
            <Button variant="outline" size="sm" onClick={load} data-testid="email-domain-refresh"
                    className="rounded-full h-8 px-3 text-[12px]">
              <RefreshCcw className="w-3.5 h-3.5 mr-1"/>Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={disconnect} disabled={busy}
                    data-testid="email-domain-disconnect"
                    className="rounded-full h-8 px-3 text-[12px] text-rose-700 border-rose-200 hover:bg-rose-50 ml-auto">
              <Trash2 className="w-3.5 h-3.5 mr-1"/>Disconnect
            </Button>
          </div>

          {(state.status === "failure" || state.status === "failed") && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-800 px-3 py-2 text-[11.5px] flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5"/>
              <div>
                DNS verification failed. Double-check that every record above is published on your
                DNS provider without extra quotes or wrapping in a container name. Then click Verify DNS again.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CopyableCell({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Copy failed — select the text manually"); }
  };
  return (
    <span className="inline-flex items-center gap-1 group">
      <span className="truncate max-w-[220px] inline-block align-middle">{value}</span>
      <button onClick={copy} className="opacity-40 group-hover:opacity-100 hover:text-foreground transition-opacity"
              title="Copy" type="button">
        {copied ? <CheckCircle2 className="w-3 h-3 text-emerald-600"/> : <Copy className="w-3 h-3"/>}
      </button>
    </span>
  );
}
